import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { orderedImpacts } from '@upright/common/model/types';
import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { ImpactTitle } from '@upright/ui/common/upright/core/impact-lens-profile/frame/impact-titles/impact-title';
import {
  buildNetImpactDrillInPayload,
  opensImpactDrillIn,
} from '@upright/ui/common/upright/core/impact-lenses/net-impact/drill-in-panel/build-payload';
import { openNetImpactDrillIn } from '@upright/ui/common/upright/core/impact-lenses/net-impact/drill-in-panel/state';
import { NetImpactLensSelectionState } from '@upright/ui/common/upright/core/impact-lenses/net-impact/selection-state';
import {
  NetImpactLensData,
  NetImpactLensOptions,
} from '@upright/ui/common/upright/core/impact-lenses/net-impact/types';
import { useReleaseFeatureGate } from '@upright/ui/common/upright/model/output/releases/hooks';
import { useCarouselContext } from '../../context';

export function ImpactTitles<A extends ImpactLensType>() {
  const context = useCarouselContext<A>();
  const selectionState = context.selectionState;
  const lens = context.lens;
  const pushGtmEvent = usePushGtmEvent();

  // PLT-4940: clicking an impact-category label in the middle of the
  // net-impact chart opens the SAME drill-in side panel as clicking the
  // adjacent bar (PLT-4915 wired the bar; the label was left routing into
  // chat, which is the inconsistency Janne reported). The label maps 1:1
  // to an impact code, so it builds an `impact`-grain payload.
  //
  // PLT-5427: the panel now opens for everyone (the per-user variation
  // lives inside the panel's Chat tab — composer / upsell / hidden), so
  // the only guard left here is the net-impact lens, matching
  // `impact-bars.tsx`. The label and the adjacent bar stay in lockstep.
  const entity = context.data.entity;
  const isNetImpactLens = context.lensType === ImpactLensType.UPRIGHT_NET_IMPACT;
  const panelGateOpen = isNetImpactLens;
  // Mirror the impact-bar's value-chain gate so the drill-in panel's
  // drilldown tables split by value chain exactly when the chart does.
  const allowValueChains = useReleaseFeatureGate('value-chain-splits');
  const showValueChains = Boolean((selectionState as NetImpactLensSelectionState).showValueChains) && allowValueChains;

  const titles = orderedImpacts.map((impactCode, _index) => {
    const isHighLighted = lens.isImpactHighlighted ? lens.isImpactHighlighted(selectionState, impactCode) : false;

    const isClickable = (() => {
      if (context.options.disableImpactTitleInteraction) return false;
      if (lens.isImpactTitleClickable === undefined) return true;
      return lens.isImpactTitleClickable(context.data, impactCode);
    })();

    function handleImpactClick() {
      pushGtmEvent({
        event: CustomGtmEvent.IMPACT_PROFILE_IMPACT_TITLE_CLICK,
        data: { impactCode, lensType: context.lensType },
      });
      if (!isClickable) return;

      // Narrow the generic carousel context to the net-impact lens
      // shapes. `context` is generic over the lens type `A`, so unlike
      // the non-generic `impact-bars.tsx` cast we go through `unknown`
      // (the cast is only read once `isNetImpactLens` holds).
      const netData = context.data as unknown as NetImpactLensData;
      if (panelGateOpen && entity && opensImpactDrillIn(netData)) {
        const netOptions = context.options as unknown as NetImpactLensOptions;
        // Land on the "About" tab so clicking the category *text* answers
        // "what does this category mean?" — the mental model the old
        // standalone explanation modal set (PLT-5714). The adjacent bars
        // still land on their matching valence tab (see `impact-bars.tsx`),
        // so the whole row opens the same panel and only the starting tab
        // differs.
        openNetImpactDrillIn({
          payload: buildNetImpactDrillInPayload({
            target: { grain: 'impact', impactCode },
            entity: { id: entity.id, type: entity.type, name: entity.name },
            scoreReader: netOptions.scoreReader,
            revenue: netData.revenue,
            modelResultId: netData.modelResultId,
            productNames: netData.productNames,
            hasProductViewAccess: netData.hasProductViewAccess,
            showValueChains,
          }),
          initialTab: 'about',
        });
        return;
      }

      context.setModalImpactCode(impactCode);
      if (lens.onImpactTitleClick) {
        const state = lens.onImpactTitleClick(selectionState, impactCode);
        context.setSelectionState(state);
      }
    }

    return (
      <ImpactTitle
        key={impactCode}
        impact={impactCode}
        handleImpactClick={handleImpactClick}
        isSelected={isHighLighted}
        isClickable={isClickable}
      />
    );
  });

  return <g data-testid={E2eTestElementId.IMPACT_TITLES}>{titles}</g>;
}
